import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';
import { ConfigService } from '../../shared-services/config.service';
import { DataErrorComponent } from '../../global-components/data-error/data-error.component';
import { auditTime, filter, Subject, Subscription, takeUntil } from 'rxjs';
import { GlobalService } from '../../shared-services/global.service';

@Component({
  selector: 'app-line-column-chart',
  standalone: true,
  imports: [CommonModule, DataErrorComponent],
  templateUrl: './line-column-chart.component.html',
  styleUrl: './line-column-chart.component.scss'
})
export class LineColumnChartComponent implements OnDestroy {
  @Input() data!: any;
  private myChart!: echarts.ECharts;
  public hasError = false;
  public errorMessage = '';
  private unSubscribe$: Subject<void> = new Subject();
  private chartData: any = null;
  private selectedItems: Map<string, boolean> = new Map();
  private resetSub!: Subscription;
  constructor(
    private configService: ConfigService,
    private filterState: GlobalService,
  ) { }

  private destroy$ = new Subject<void>();

  ngAfterViewInit() {
    this.filterState.filters$
      .pipe(
        // short delay to collect rapid emissions
        auditTime(1000),
        filter((filters) => filters !== null && filters !== undefined),
        takeUntil(this.destroy$)
      )
      .subscribe(filters => {
        if (filters?.widgetId === this.data.widgetId) return;
        setTimeout(() => this.loadAndRenderChart(filters.filter), 0);
      });
    this.resetSub = this.filterState.reset$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.clearSelections();
      });
  }

  private clearSelections(): void {
    // Clear internal selection tracking
    if (this.selectedItems) {
      this.selectedItems.forEach((_, key) => {
        const [seriesIndex, dataIndex] = key.split('-').map(Number);
        this.myChart?.dispatchAction({
          type: 'unselect',
          seriesIndex,
          dataIndex,
        });
      });
      this.selectedItems.clear();
    }
  }


  private loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data?.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
    }
    this.myChart.showLoading();
    this.configService.fetchData(this.data.apiUrl, this.data.functionName, filters, this.data)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res) => {
          this.chartData = this.data?.responseKey ? res?.responseList?.[0]?.responseData?.[0][this.data.responseKey] : res;
          // Handle empty or invalid data
          const isEmpty =
            !this.chartData ||
            !this.chartData.categories?.length;


          if (isEmpty) {
            this.hasError = true;
            this.errorMessage = 'No data found for the selected filters.';
            this.myChart.clear();
          } else {
            this.hasError = false;
            this.renderChart(this.chartData);
          }

          this.myChart.hideLoading();
          window.addEventListener('resize', this.resizeChart);
        },
        error: (err) => {
          this.hasError = true;
          this.errorMessage = 'No Data available for the chart.';
          this.myChart?.clear();
          this.myChart.hideLoading();
        }
      });
  }

  private renderChart(fullData: any): void {
    const series = this.data.series;

    const barMaxAbs = Math.max(
      1e-9,
      ...series
        .filter((s: any) => s.type === 'bar')
        .flatMap((s: any) => fullData.values[s.dataKey])
        .map((v: any) => Math.abs(v))
    );

    const option = {
      tooltip: { trigger: 'axis' },
      legend: {
        data: this.data.series.map((s: any) => s.name)
      },
      xAxis: {
        type: 'category',
        data: fullData.categories,
        axisLabel: {
          interval: 0,
          hideOverlap: true,
          formatter: (value: string) => value.split(" ").join("\n")
        }
      },
      yAxis: this.data.series.map((s: any, index: number) => ({
        type: "value",
        name: s.name,
        splitLine: { show: index === 0 },
        axisLabel: { show: index === 0 },
        min: index == 1 ? -0.5 * barMaxAbs : null,
        max: index == 1 ? Math.max(...fullData.values[s.dataKey]) : null,
      })),
      dataZoom: this.data.enableScroll
        ? [
          {
            type: 'slider',
            xAxisIndex: 0,
            height: 15,
            bottom: 4,
            start: 0,
            end: 30
          },
        ]
        : [],
      series: this.data.series.map((s: any, index: number) => ({
        name: s.name,
        type: s.type,
        data: fullData.values[s.dataKey],
        yAxisIndex: s.yAxisIndex,
        barWidth: 20,
        color: s.color,
        // Allow multi-select
        selectedMode: 'multiple',
        select: {
          itemStyle: {
            color: '#ff7f50',
            borderColor: '#000',
            borderWidth: 2
          }
        },
        // emphasis: {
        //   itemStyle: {
        //     color: '#908c8bff',
        //     borderColor: '#000',
        //     borderWidth: 2
        //   }
        // },
        label: {
          show: index === 1,
          formatter: (params: any) => {
            const val = typeof params.value === 'number' ? params.value : Number(params.value);
            return isNaN(val) ? '-' : val.toFixed(1);
          }
        }
      }))
    };

    this.myChart.setOption(option);

    // --- Multi-select logic ---
    this.myChart.off('click');
    this.myChart.on('click', (params: any) => {
      if (params.seriesType === 'bar' || params.seriesType === 'line') {
        const { seriesIndex, dataIndex, name } = params;
        const key = `${seriesIndex}-${dataIndex}`;

        // Initialize selection map if missing
        if (!this.selectedItems) this.selectedItems = new Map<string, boolean>();

        const isAlreadySelected = this.selectedItems.get(key);

        if (isAlreadySelected) {
          // Unselect the clicked bar
          this.myChart.dispatchAction({
            type: 'unselect',
            seriesIndex,
            dataIndex,
          });
          this.selectedItems.delete(key);
        } else {
          // Select the clicked bar
          this.myChart.dispatchAction({
            type: 'select',
            seriesIndex,
            dataIndex,
          });
          this.selectedItems.set(key, true);
        }

        const selectedLabels = Array.from(this.selectedItems.keys())
          .map(k => {
            const [sIdx, dIdx] = k.split('-').map(Number);
            const label = fullData?.categories?.[dIdx];
            return typeof label === 'string' && label.trim() !== '' ? label : null;
          })
          .filter((label): label is string => label !== null);

        // Update filter state safely
        this.filterState.updateFilterSelection(
          { [this.data.key]: selectedLabels },
          this.data.widgetId
        );
      }
    });
  }

  private resizeChart = () => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    this.myChart?.dispose();
    window.removeEventListener('resize', this.resizeChart);
    this.destroy$.next();
    this.destroy$.complete();
    this.resetSub.unsubscribe();
  }
}